import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, TextField, Alert, Chip, Avatar, Paper,
  IconButton, Tooltip, Stack, CircularProgress, Divider, useTheme
} from '@mui/material';
import {
  QrCodeScanner as QrScannerIcon, Close as CloseIcon,
  CheckCircle as CheckIcon, Warning as WarningIcon,
  FlipCameraAndroid as SwitchCamIcon, FlashOn as FlashIcon,
  Person as PersonIcon, VolumeUp as SoundIcon, VolumeOff as SoundOffIcon
} from '@mui/icons-material';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Student, Session } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

interface ScannedRecord {
  student: Student;
  timestamp: string;
  success: boolean;
  message?: string;
}

interface QrAttendanceScannerModalProps {
  open: boolean;
  onClose: () => void;
  currentSession: Session | null;
  onStudentScanned: (student: Student) => Promise<{ success: boolean; message?: string }>;
  allStudents: Student[];
}

export const QrAttendanceScannerModal: React.FC<QrAttendanceScannerModalProps> = ({
  open,
  onClose,
  currentSession,
  onStudentScanned,
  allStudents
}) => {
  const { t } = useLanguage();
  const theme = useTheme();

  const [manualCode, setManualCode] = useState('');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [scannedHistory, setScannedHistory] = useState<ScannedRecord[]>([]);
  const [lastScannedResult, setLastScannedResult] = useState<ScannedRecord | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const isStoppingRef = useRef(false);
  const manualInputRef = useRef<HTMLInputElement>(null);

  // Play audio chime feedback
  const playSound = (success: boolean) => {
    if (!soundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (success) {
        osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.08); // A5
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
        osc.start();
        osc.stop(ctx.currentTime + 0.25);
      } else {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(220, ctx.currentTime); // A3
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
      }
    } catch {
      // AudioContext fallback silent
    }
  };

  // Find student by decoded code / UID
  const findStudentByCode = (rawCode: string): Student | null => {
    if (!rawCode) return null;
    const clean = rawCode.trim();

    // Match BLO-STD-{UID} or BLO-STD-{ID}
    let targetUid = clean;
    if (clean.startsWith('BLO-STD-')) {
      targetUid = clean.replace('BLO-STD-', '');
    } else if (clean.startsWith('STD-')) {
      targetUid = clean.replace('STD-', '');
    }

    return allStudents.find(s => {
      const sUid = String(s.uid || s.STD_UIDF || '').trim();
      const sId = String(s.id).trim();
      const sCode = String(s.code || s.STD_CODE || '').trim();
      return (
        (sUid && sUid.toLowerCase() === targetUid.toLowerCase()) ||
        (sId && sId === targetUid) ||
        (sCode && sCode.toLowerCase() === clean.toLowerCase()) ||
        (sUid && sUid.toLowerCase() === clean.toLowerCase()) ||
        (sId && sId === clean)
      );
    }) || null;
  };

  // Process decoded code
  const handleProcessCode = async (code: string) => {
    if (!code || isProcessing) return;
    setIsProcessing(true);

    const student = findStudentByCode(code);
    const now = new Date().toLocaleTimeString();

    if (!student) {
      const rec: ScannedRecord = {
        student: { id: 0, first_name: code, last_name: '', category: 'adult', date_of_birth: '', emergency_contact: '' },
        timestamp: now,
        success: false,
        message: `Aucun étudiant trouvé pour le code "${code}"`
      };
      setLastScannedResult(rec);
      setScannedHistory(prev => [rec, ...prev.slice(0, 10)]);
      playSound(false);
      setIsProcessing(false);
      return;
    }

    const res = await onStudentScanned(student);
    const rec: ScannedRecord = {
      student,
      timestamp: now,
      success: res.success,
      message: res.message || (res.success ? t('academy.attendance.mark_present_success', `${student.first_name} ${student.last_name}`) : undefined)
    };

    setLastScannedResult(rec);
    setScannedHistory(prev => [rec, ...prev.slice(0, 10)]);
    playSound(res.success);

    setTimeout(() => {
      setIsProcessing(false);
    }, 1200);
  };

  // Handle Manual Code Submit
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleProcessCode(manualCode.trim());
      setManualCode('');
    }
  };

  const stopCamera = async () => {
    if (html5QrCodeRef.current && !isStoppingRef.current) {
      isStoppingRef.current = true;
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch {
        // silent cleanup
      } finally {
        html5QrCodeRef.current = null;
        setIsScanning(false);
        isStoppingRef.current = false;
      }
    }
  };

  const startCamera = async () => {
    await stopCamera();
    setCameraError(null);
    setLastScannedResult(null);

    // Focus manual input
    manualInputRef.current?.focus();

    const qrContainerId = 'qr-attendance-reader';
    const container = document.getElementById(qrContainerId);
    if (!container) {
      // If container not yet in DOM, retry shortly
      setTimeout(startCamera, 150);
      return;
    }

    try {
      const html5QrCode = new Html5Qrcode(qrContainerId, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.EAN_13
        ],
        verbose: false
      });
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 220, height: 220 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          handleProcessCode(decodedText);
        },
        () => {
          // frame decode failure (ignore)
        }
      );
      setIsScanning(true);
    } catch (err: any) {
      console.warn('Camera start error:', err);
      setCameraError(t('academy.attendance.camera_start_error') || "Accès caméra indisponible ou refusé.");
      setIsScanning(false);
    }
  };

  const handleClose = async () => {
    await stopCamera();
    onClose();
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{
        onEntered: startCamera,
        onExited: stopCamera
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5, bgcolor: theme.palette.primary.main, color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <QrScannerIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('academy.attendance.scanner_title') || 'Scanner de Présence QR'}
            </Typography>
            {currentSession && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Session: {currentSession.date} ({currentSession.start_time} - {currentSession.end_time})
              </Typography>
            )}
          </Box>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={soundEnabled ? "Son activé" : "Son désactivé"}>
            <IconButton size="small" onClick={() => setSoundEnabled(!soundEnabled)} sx={{ color: '#fff' }}>
              {soundEnabled ? <SoundIcon /> : <SoundOffIcon />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          {/* Active Result Notification Banner */}
          {lastScannedResult && (
            <Alert
              severity={lastScannedResult.success ? 'success' : 'warning'}
              icon={lastScannedResult.success ? <CheckIcon fontSize="large" /> : <WarningIcon fontSize="large" />}
              sx={{
                borderRadius: 2,
                boxShadow: 2,
                alignItems: 'center',
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lastScannedResult.student.first_name} {lastScannedResult.student.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {lastScannedResult.message}
                  </Typography>
                </Box>
                <Chip
                  label={lastScannedResult.timestamp}
                  size="small"
                  color={lastScannedResult.success ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Alert>
          )}

          {/* Scanner Main Box with Video Feed & Manual Barcode Input */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
            {/* Camera Viewfinder */}
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minHeight: 280,
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                bgcolor: '#0f172a',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div id="qr-attendance-reader" style={{ width: '100%', height: '100%' }} />

              {cameraError && (
                <Box sx={{ p: 3, textAlign: 'center', color: '#fff' }}>
                  <WarningIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2" color="error.light">
                    {cameraError}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
                    Utilisez le champ de saisie manuelle ci-dessous ou un lecteur code-barres USB.
                  </Typography>
                </Box>
              )}

              {/* Laser animation indicator while scanning */}
              {isScanning && !cameraError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '20%',
                    left: '15%',
                    right: '15%',
                    height: '2px',
                    bgcolor: '#22c55e',
                    boxShadow: '0 0 12px 3px rgba(34, 197, 94, 0.8)',
                    zIndex: 2,
                    pointerEvents: 'none',
                    animation: 'scanLaser 2s infinite ease-in-out',
                    '@keyframes scanLaser': {
                      '0%': { top: '20%' },
                      '50%': { top: '80%' },
                      '100%': { top: '20%' }
                    }
                  }}
                />
              )}
            </Paper>

            {/* Manual input & Live feed summary */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Barcode gun auto-focused input form */}
              <form onSubmit={handleManualSubmit}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Entrée Douchette USB / Saisie Manuelle
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    inputRef={manualInputRef}
                    size="small"
                    fullWidth
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder={t('academy.attendance.manual_code_placeholder') || 'Code (ex: BLO-STD-...)...'}
                    disabled={isProcessing}
                    autoComplete="off"
                    autoFocus
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!manualCode.trim() || isProcessing}
                  >
                    Valider
                  </Button>
                </Box>
              </form>

              <Divider />

              {/* Scanned Roster Live Feed */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Historique du pointage</span>
                  <Chip label={`${scannedHistory.filter(h => h.success).length} pointés`} size="small" color="primary" />
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    maxHeight: 180,
                    overflowY: 'auto',
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'background.default'
                  }}
                >
                  {scannedHistory.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="caption">
                        En attente du premier badge à scanner...
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0.8}>
                      {scannedHistory.map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: item.success ? 'success.50' : 'warning.50',
                            border: '1px solid',
                            borderColor: item.success ? 'success.light' : 'warning.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              src={item.student.photo_base64}
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: '0.75rem',
                                bgcolor: item.success ? 'success.main' : 'warning.main'
                              }}
                            >
                              {!item.student.photo_base64 && (item.student.first_name?.[0] || '?')}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                                {item.student.first_name} {item.student.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.message || (item.success ? 'Présent' : 'Non inscrit')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                            {item.timestamp}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('btn.close') || 'Fermer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
